import {
  type AnchorHTMLAttributes,
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { DEFAULT_LANGUAGE, withLanguage } from "./i18n/languages.js";

export interface AppLocation {
  readonly pathname: string;
  readonly search: string;
}

const NAVIGATED = "skillcdn:navigated";

const LocationContext = createContext<AppLocation>({ pathname: "/", search: "" });

function currentLocation(): AppLocation {
  return { pathname: window.location.pathname, search: window.location.search };
}

/**
 * Moves to another page of the app without loading a document. A new page starts at its top,
 * at once: an animated scroll would show the old page leaving. A return through history keeps
 * the browser's own restoration, which is instant as well.
 */
export function navigate(href: string, options: { readonly replace?: boolean } = {}): void {
  if (options.replace === true) {
    window.history.replaceState(null, "", href);
  } else {
    window.history.pushState(null, "", href);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }
  window.dispatchEvent(new Event(NAVIGATED));
}

/** Holds the location: the one given while prerendering, the browser's afterwards. */
export function LocationProvider(props: {
  readonly initial: AppLocation;
  readonly children: ReactNode;
}) {
  const [location, setLocation] = useState(props.initial);
  useEffect(() => {
    const update = () => setLocation(currentLocation());
    window.addEventListener("popstate", update);
    window.addEventListener(NAVIGATED, update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener(NAVIGATED, update);
    };
  }, []);
  return <LocationContext.Provider value={location}>{props.children}</LocationContext.Provider>;
}

export function useLocation(): AppLocation {
  return useContext(LocationContext);
}

/**
 * An href inside the app: the location without a language, so that a copied link opens in each
 * reader's own language. The language a page is shown in is the visitor's preference, which a
 * forcing URL or the switcher sets (ADR-0015, ADR-0017).
 */
export function appHref(href: string): string {
  return withLanguage(href, DEFAULT_LANGUAGE);
}

function isPlainLeftClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * A link to a page of the app. It is a real anchor with a real href, without a language, so it
 * works without scripts, for crawlers, and with "open in new tab".
 */
export function Link(
  props: AnchorHTMLAttributes<HTMLAnchorElement> & {
    readonly href: string;
    /** Keep the href as it is, for links that change the language. */
    readonly exact?: boolean;
  },
) {
  const { href, exact, onClick, children, ...rest } = props;
  const target = exact === true ? href : appHref(href);
  return (
    <a
      {...rest}
      href={target}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || !isPlainLeftClick(event) || rest.target !== undefined) {
          return;
        }
        event.preventDefault();
        navigate(target);
      }}
    >
      {children}
    </a>
  );
}
